import { AccountControllerApi } from '@/api';

import {
  adaptPotentialTerminationReasonsResponseToPotentialTerminationReasons,
  adaptQuestionsResponseToLoginFormFields,
} from '@/adapters';

import {
  fetchCaqhInfoSuccess,
  fetchPlanListSuccess,
  fetchPotentialTerminationReasonsSuccess,
  fetchAuthFailure,
  fetchQuestionsSuccess,
  loginFailure,
  loginLoading,
  loginSuccess,
  setIdentity,
  setPotentialTerminationDetail,
  setTerminationSkipped,
} from '.';
import { AppDispatch } from '@/store';
import { LoginRequestDTO } from '@/api/account-controller/DTOs';
import { adaptPotentialTerminationReasonDetailsToSetTerminationDetailRequest } from '@/adapters';
import {
  AuthKey,
  Identity,
  Plan,
  PotentialTerminationReasonDetailExplanation,
} from '../typings';
import { AuthService } from '..';
import { isInvitationLocked } from '../utils/isInvitationLocked';
import { toStoreError } from '@/types/storeError.interface';

export const fetchLoginQuestions =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getQuestions({ key });

    if (result.success) {
      dispatch(
        fetchQuestionsSuccess({
          key,
          questions: adaptQuestionsResponseToLoginFormFields(
            result.data.questions,
          ),
          providerFullName: result.data.providerFullName,
          providerAddress: result.data.providerAddress,
          taxonomyName: result.data.taxonomyName,
          isInstitution: result.data.isInstitution,
        }),
      );
    } else {
      console.error('Failed to fetch questions:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const login =
  (
    key: AuthKey,
    answers: LoginRequestDTO['answers'],
    userData: LoginRequestDTO['userData'],
    challenge?: LoginRequestDTO['challenge'],
  ) =>
    async (dispatch: AppDispatch) => {
      dispatch(loginLoading());

      // The proof goes to the request only; the auth slice is persisted.
      const result = await new AccountControllerApi().login({
        key,
        answers,
        userData,
        challenge,
      });

      if (result.success) {
        dispatch(loginSuccess({ key, token: result.data.token }));
        new AuthService().loginAlert();
      } else {
        console.error('Login failed:', result.error);
        const error = toStoreError(result.error);
        // The lock belongs to this key; the global error is shared by every key and persisted.
        if (isInvitationLocked(error)) {
          dispatch(fetchAuthFailure({ key, error }));
        } else {
          dispatch(loginFailure(error));
        }
        return error;
      }
    };

export const fetchIdentity =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getIdentity({ key });

    if (result.success) {
      dispatch(
        setIdentity({
          key,
          identity: result.data.identityUpdatedDate
            ? result.data.isProvider
              ? Identity.Provider
              : Identity.Representative
            : undefined,
        }),
      );
    } else {
      console.error('Failed to fetch identity:', result.error);
      dispatch(fetchAuthFailure({ key, error: toStoreError(result.error) }));
    }
  };

export const updateIdentity =
  (key: AuthKey, newIdentity: Identity) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().setIdentity({
      key,
      isProvider: newIdentity === Identity.Provider,
    });

    if (result.success && result.data.identityUpdatedDate) {
      dispatch(
        setIdentity({
          key,
          identity: result.data.isProvider
            ? Identity.Provider
            : Identity.Representative,
        }),
      );
    } else if (!result.success) {
      console.error('Failed to update identity:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const updatePotentialTerminationReason =
  (key: AuthKey, detail: PotentialTerminationReasonDetailExplanation) =>
    async (dispatch: AppDispatch) => {
      dispatch(loginLoading());

      const result = await new AccountControllerApi().setTerminationDetail({
        key,
        detail:
          adaptPotentialTerminationReasonDetailsToSetTerminationDetailRequest(
            detail,
          ),
      });

      if (result.success) {
        dispatch(
          setPotentialTerminationDetail({
            key,
            terminationDetail: {
              reason: result.data.reason!,
              terminatedAt: result.data.terminatedDate!,
              applicationFormEnabled: result.data.applicationFormEnabled,
            },
          }),
        );
      } else {
        console.error('Failed to set termination detail:', result.error);
        const error = toStoreError(result.error);
        dispatch(fetchAuthFailure({ key, error }));
        return error;
      }
    };

export const fetchPotentialTerminationDetail =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getTerminationDetail({
      key,
    });

    if (result.success) {
      dispatch(
        setPotentialTerminationDetail({
          key,
          terminationDetail: result.data.terminatedDate
            ? {
              reason: result.data.reason!,
              terminatedAt: result.data.terminatedDate!,
              applicationFormEnabled: result.data.applicationFormEnabled,
            }
            : undefined,
        }),
      );
    } else {
      console.error('Failed to fetch termination detail:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const fetchPotentialTerminationReasons =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result =
      await new AccountControllerApi().getPotentialTerminationReasons({ key });

    if (result.success) {
      dispatch(
        fetchPotentialTerminationReasonsSuccess(
          adaptPotentialTerminationReasonsResponseToPotentialTerminationReasons(
            result.data,
          ),
        ),
      );
    } else {
      console.error(
        'Failed to fetch potential termination reasons:',
        result.error,
      );
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const skipTermination =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().setTerminationSkipped({
      key,
    });

    if (result.success) {
      dispatch(
        setTerminationSkipped({
          key,
          terminationSkipped: result.data.skippedDate ? true : false,
        }),
      );
    } else {
      console.error('Failed to skip termination:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const fetchSkippedTermination =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getTerminationSkipped({
      key,
    });

    if (result.success) {
      dispatch(
        setTerminationSkipped({
          key,
          // Missing-item-only providers have no application form, so there is no
          // termination gate to clear — treat it as already resolved.
          terminationSkipped: result.data.skippedDate
            ? true
            : !result.data.hasApplicationForm,
        }),
      );
    } else {
      console.error('Failed to fetch termination skipped:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const fetchCaqhInfo =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getCaqhInfo({ key });

    if (result.success) {
      // TODO: Create an adapter for this
      dispatch(
        fetchCaqhInfoSuccess({
          key,
          caqhInfo: {
            caqhEnabled: result.data.caqhEnabled,
            caqhIdNumber: result.data.caqhIdNumber,
          },
        }),
      );
    } else {
      console.error('Failed to fetch CAQH info:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };

export const fetchPlanList =
  (key: AuthKey) => async (dispatch: AppDispatch) => {
    dispatch(loginLoading());

    const result = await new AccountControllerApi().getClientPlans({ key });

    if (result.success) {
      dispatch(
        fetchPlanListSuccess({
          key,
          planList: result.data.clientPlans.map(plan => ({
            id: plan.planId,
            name: plan.planName,
          })) as Plan[],
          providerFullName: result.data.providerFullName,
          providerAddress: result.data.providerAddress,
          taxonomyName: result.data.taxonomyName,
          isInstitution: result.data.isInstitution,
        }),
      );
    } else {
      console.error('Failed to fetch plan list:', result.error);
      const error = toStoreError(result.error);
      dispatch(fetchAuthFailure({ key, error }));
      return error;
    }
  };
